import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './styles/scheduled.module.css';
import sharedStyles from './styles/gameCard.module.css';
import { useGameContext } from '../../context/GameContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { formatGameDateTime } from '../../utils/timeUtils';
import { formatLine } from '../../utils/lineDataUtils';
import BettingButton from './BettingButton';
import LockStatusBars from './LockStatusBars';

const ScheduledGameCard = ({
  game,
  displayedWeek,
  watchedPicks,
  userPick,
  className,
  onOpenGameDetail,
  ...props
}) => {
  const {
    watchLoading = {},
    watchError = {},
    locksCache = {},
    user,
    getMaxPicksForWeek,
    getLocksForWeek,
    lineChanges,
  } = useGameContext();

  const { canSeeLocks } = useLeagueSettings();

  const hasSpread = game.betting?.spread != null;
  const hasOverUnder = game.betting?.over_under != null;
  const homeSpread = hasSpread ? formatLine(game.betting.spread) : 'N/A';
  const awaySpread = hasSpread ? formatLine(-game.betting.spread) : 'N/A';

  const generatePickId = useCallback((gameId, pickType) => `${gameId}_${pickType}`, []);

  const isWatched = useCallback((pickType) => {
    const pickId = generatePickId(game.id, pickType);
    return watchedPicks.some(pick => pick.id === pickId);
  }, [generatePickId, game.id, watchedPicks]);

  const isLoading = useCallback((pickType) => {
    const pickId = generatePickId(game.id, pickType);
    return watchLoading[pickId] || false;
  }, [generatePickId, game.id, watchLoading]);

  const error = useCallback((pickType) => {
    const pickId = generatePickId(game.id, pickType);
    return watchError[pickId] || null;
  }, [generatePickId, game.id, watchError]);

  const getPickLines = useCallback((pickType) => {
    const liveLine = pickType === 'over' || pickType === 'under'
      ? game.betting.over_under
      : game.betting.spread;

    if (liveLine === null) return null;
    return { liveLine };
  }, [game.betting]);

  const handlePickClick = useCallback((pickType, event) => {
    event?.stopPropagation?.();
    if (!getPickLines(pickType) || !onOpenGameDetail) return;
    onOpenGameDetail(game, pickType);
  }, [getPickLines, onOpenGameDetail, game]);

  const handleBrowseOpen = useCallback(() => {
    onOpenGameDetail?.(game, null);
  }, [onOpenGameDetail, game]);

  const handleKeyDown = useCallback((event, pickType) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePickClick(pickType, event);
    }
  }, [handlePickClick]);

  const handleBrowseKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBrowseOpen();
    }
  }, [handleBrowseOpen]);

  const hasReachedMaxPicks = useCallback(() => {
    const locksForWeek = getLocksForWeek(displayedWeek);
    const maxPicksForWeek = getMaxPicksForWeek(displayedWeek);

    if (!locksForWeek || !Array.isArray(locksForWeek)) return false;

    const userLocksCount = locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    return userLocksCount >= maxPicksForWeek;
  }, [getLocksForWeek, displayedWeek, user, getMaxPicksForWeek]);

  const isLocked = useCallback((pickType) => {
    if (!canSeeLocks() && !hasReachedMaxPicks()) {
      return false;
    }

    const locksForWeek = locksCache[displayedWeek] || [];
    return locksForWeek.some(lock =>
      lock.gameId === game.id &&
      lock.pickType === pickType &&
      lock.userId === user?.id
    );
  }, [game.id, displayedWeek, locksCache, user, canSeeLocks, hasReachedMaxPicks]);

  const isOtherLocked = useCallback((pickType) => {
    if (!canSeeLocks() && !hasReachedMaxPicks()) {
      return false;
    }

    const locksForWeek = locksCache[displayedWeek] || [];
    return locksForWeek.some(lock =>
      lock.gameId === game.id &&
      lock.pickType === pickType &&
      lock.userId !== user?.id
    );
  }, [game.id, displayedWeek, locksCache, user, canSeeLocks, hasReachedMaxPicks]);

  const hasRecentLineChange = useCallback((pickType) => {
    const change = lineChanges[game.id];
    if (!change || Date.now() - change.timestamp >= 2000) return false;

    return pickType === 'over' || pickType === 'under'
      ? change.overUnder
      : change.spread;
  }, [lineChanges, game.id]);

  const { displayDate, displayTime } = formatGameDateTime(game.start_date);
  const gameLocks = locksCache[displayedWeek]?.filter(lock => lock.gameId === game.id) || [];

  return (
    <div
      className={`${styles.gameCard} ${sharedStyles.gameCardContainer} ${className || ''}`}
      data-game-id={game.id}
    >
      <button
        type="button"
        className={`${styles.header} ${styles.browseHeader}`}
        onClick={handleBrowseOpen}
        onKeyDown={handleBrowseKeyDown}
        aria-label={`Open details for ${game.away_team.name} at ${game.home_team.name}`}
      >
        <span className={styles.gameInfo}>
          {displayDate} • {displayTime} • {game.tv}
        </span>
      </button>

      <div className={styles.columns}>
        <div className={styles.column}></div>
        <div className={styles.column}>SPREAD</div>
        <div className={styles.column}>TOTAL</div>
      </div>

      <div className={styles.teamRow}>
        <div className={styles.teamInfo}>
          <button
            type="button"
            className={`${styles.teamName} ${styles.browseTarget}`}
            onClick={handleBrowseOpen}
            onKeyDown={handleBrowseKeyDown}
            aria-label={`Open details for ${game.away_team.name}`}
          >
            {game.away_team.rank && (
              <span className={sharedStyles.rank}>
                ({game.away_team.rank})
              </span>
            )}
            <span className={styles.schoolName}>{game.away_team.name}</span>
          </button>
          <span className={styles.record}>{game.away_team.record}</span>
        </div>

        <div className={styles.spreadColumn}>
          <BettingButton
            pickType="away"
            displayValue={awaySpread}
            isWatched={isWatched('away')}
            isLocked={isLocked('away')}
            isOtherLocked={isOtherLocked('away')}
            hasReachedMaxPicks={hasReachedMaxPicks()}
            hasRecentLineChange={hasRecentLineChange('away')}
            isDisabled={!hasSpread}
            isLoading={isLoading('away')}
            onClick={(e) => handlePickClick('away', e)}
            onKeyDown={(e) => handleKeyDown(e, 'away')}
            ariaLabel={`Pick ${game.away_team.name} ${awaySpread}`}
            weekNumber={displayedWeek}
            hasUserReachedMaxLocks={hasReachedMaxPicks()}
          />
        </div>

        <div className={styles.totalColumn}>
          <BettingButton
            pickType="over"
            displayValue={hasOverUnder ? `O ${game.betting.over_under}` : 'N/A'}
            isWatched={isWatched('over')}
            isLocked={isLocked('over')}
            isOtherLocked={isOtherLocked('over')}
            hasReachedMaxPicks={hasReachedMaxPicks()}
            hasRecentLineChange={hasRecentLineChange('over')}
            isDisabled={!hasOverUnder}
            isLoading={isLoading('over')}
            onClick={(e) => handlePickClick('over', e)}
            onKeyDown={(e) => handleKeyDown(e, 'over')}
            weekNumber={displayedWeek}
            ariaLabel={`Pick Over ${hasOverUnder ? game.betting.over_under : 'not available'}`}
            hasUserReachedMaxLocks={hasReachedMaxPicks()}
          />
        </div>
      </div>

      <div className={styles.teamRow}>
        <div className={styles.teamInfo}>
          <button
            type="button"
            className={`${styles.teamName} ${styles.browseTarget}`}
            onClick={handleBrowseOpen}
            onKeyDown={handleBrowseKeyDown}
            aria-label={`Open details for ${game.home_team.name}`}
          >
            {game.home_team.rank && (
              <span className={sharedStyles.rank}>
                ({game.home_team.rank})
              </span>
            )}
            <span className={styles.schoolName}>{game.home_team.name}</span>
          </button>
          <span className={styles.record}>{game.home_team.record}</span>
        </div>

        <div className={styles.spreadColumn}>
          <BettingButton
            pickType="home"
            displayValue={homeSpread}
            isWatched={isWatched('home')}
            isLocked={isLocked('home')}
            isOtherLocked={isOtherLocked('home')}
            hasReachedMaxPicks={hasReachedMaxPicks()}
            hasRecentLineChange={hasRecentLineChange('home')}
            isDisabled={!hasSpread}
            isLoading={isLoading('home')}
            onClick={(e) => handlePickClick('home', e)}
            onKeyDown={(e) => handleKeyDown(e, 'home')}
            ariaLabel={`Pick ${game.home_team.name} ${homeSpread}`}
            weekNumber={displayedWeek}
            hasUserReachedMaxLocks={hasReachedMaxPicks()}
          />
        </div>

        <div className={styles.totalColumn}>
          <BettingButton
            pickType="under"
            displayValue={hasOverUnder ? `U ${game.betting.over_under}` : 'N/A'}
            isWatched={isWatched('under')}
            isLocked={isLocked('under')}
            isOtherLocked={isOtherLocked('under')}
            hasReachedMaxPicks={hasReachedMaxPicks()}
            hasRecentLineChange={hasRecentLineChange('under')}
            isDisabled={!hasOverUnder}
            isLoading={isLoading('under')}
            onClick={(e) => handlePickClick('under', e)}
            onKeyDown={(e) => handleKeyDown(e, 'under')}
            weekNumber={displayedWeek}
            ariaLabel={`Pick Under ${hasOverUnder ? game.betting.over_under : 'not available'}`}
            hasUserReachedMaxLocks={hasReachedMaxPicks()}
          />
        </div>
      </div>

      {gameLocks.length > 0 && (
        <LockStatusBars
          gameLocks={gameLocks}
          game={game}
          userId={user?.id}
          weekNumber={displayedWeek}
        />
      )}

      {(error('home') || error('away') || error('over') || error('under')) && (
        <div className={styles.errorMessage} aria-live="assertive">
          {error('home') || error('away') || error('over') || error('under')}
        </div>
      )}
    </div>
  );
};

ScheduledGameCard.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.number.isRequired,
    betting: PropTypes.shape({
      spread: PropTypes.number,
      over_under: PropTypes.number
    }).isRequired,
    start_date: PropTypes.string.isRequired,
    tv: PropTypes.string,
    start_time_tbd: PropTypes.number,
    home_team: PropTypes.shape({
      name: PropTypes.string.isRequired,
      rank: PropTypes.number,
      record: PropTypes.string
    }).isRequired,
    away_team: PropTypes.shape({
      name: PropTypes.string.isRequired,
      rank: PropTypes.number,
      record: PropTypes.string
    }).isRequired
  }).isRequired,
  userPick: PropTypes.object,
  displayedWeek: PropTypes.number.isRequired,
  watchedPicks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    weekNumber: PropTypes.number.isRequired
  })).isRequired,
  onOpenGameDetail: PropTypes.func,
  className: PropTypes.string,
};

export default ScheduledGameCard;
